import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/** Shared divider-line style for the whole page */
export const LINE = 'border-primary-200/70';

/** Major section boundary: one step stronger than component lines */
export const MAJOR_LINE = 'border-primary-300/80';

/** Shared content shell: ~80px outer margin at 1440, max 1280px */
export const SHELL = 'mx-auto w-full max-w-[1440px] px-5 md:px-10 lg:px-20';

/** Global content inset inside the frame (text & buttons only) */
export const INSET_X = 'px-6 md:px-8';
export const INSET_Y = 'py-8';

interface SectionProps {
  children: ReactNode;
  id?: string;
  className?: string;
  /** Draw a major boundary line at the bottom, aligned to the shell */
  divider?: boolean;
  /** Top outer gap zone that hosts the header (Hero only) */
  headerGap?: boolean;
  /** Larger bottom outer gap (final screen grounding) */
  groundedBottom?: boolean;
  /** Release the one-screen height lock on short viewports (< 820px tall) */
  growOnShort?: boolean;
  /** Content-driven height (no one-screen lock) */
  autoHeight?: boolean;
}

/** Each screen owns its own top + bottom full-width boundary; screens are separated by a breathing gap */
export function Section({ children, id, className = '', divider = false, headerGap = false, groundedBottom = false, growOnShort = false, autoHeight = false }: SectionProps) {
  return (
    <section
      id={id}
      className={`relative flex flex-col ${autoHeight ? '' : 'lg:h-[100svh] lg:min-h-[760px]'} ${
        growOnShort ? 'lg:[@media(max-height:819px)]:h-auto' : ''
      } ${
        'pb-10 md:pb-14'
      } ${headerGap ? 'pt-16 md:pt-20' : 'pt-10 md:pt-14'}`}
    >
      {/* Outer gaps (pt/pb) stay empty page background; the canvas below is the framed screen */}
      <div className={`relative flex flex-1 flex-col ${className}`}>
        <GlobalRule position="top" />
        {children}
        <GlobalRule position="bottom" />
      </div>
    </section>
  );
}

/** Page-level chapter rule: always full viewport width, crosses both rails */
export function GlobalRule({ position }: { position: 'top' | 'bottom' }) {
  const ref = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setDrawn(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setDrawn(true);
        io.disconnect();
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`rule-draw pointer-events-none absolute left-0 right-0 z-20 border-t ${MAJOR_LINE} ${
        position === 'top' ? 'top-0' : 'bottom-0'
      } ${drawn ? 'is-drawn' : ''} ${position === 'bottom' ? 'rule-draw-late' : ''}`}
    />
  );
}

export default function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      {children}
      {/* Permanent global outer frame: two continuous rails, top to bottom */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20">
        <div className={`${SHELL} h-full`}>
          <div className={`rail-draw h-full border-x ${LINE}`} />
        </div>
      </div>
    </div>
  );
}